import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { riskyTicked } from '../../domain/environmentRisk.js';
import { toggled } from '../../domain/toggled.js';
import { useWriteAccess } from '../../hooks/useWriteAccess.js';
import { useTRPC } from '../../utils/trpc.js';
import {
    canPush,
    confirmLabel,
    writesOf,
    type PushEnvironmentResult,
    type PushPreview,
    type PushRequest,
} from './pushSummary.js';

/**
 * Review → confirm → run, for one push request. `useWriteAccess()` already holds
 * `environments.list` and `users.me`; this reads them off it rather than asking again.
 *
 * The ticked set is what someone clicked; a preview coming back refused for one of them is
 * never written back into it — the effective set (what the checklist shows ticked, and what a
 * confirm would push) is derived by subtracting refused environments on every render, so there
 * is nothing to untick by hand and no state that can fall out of step with the last preview.
 */
export const usePushWorkflow = (request: PushRequest) => {
    const trpc = useTRPC();
    const writeAccess = useWriteAccess();
    const names = (writeAccess.environments.data ?? []).filter((e) => e.enabled).map((e) => e.name);
    const [ticked, setTicked] = useState<Set<string>>(new Set(request.environments ?? []));
    const [note, setNote] = useState('');
    const [result, setResult] = useState<PushEnvironmentResult[] | null>(null);

    // Every enabled environment is previewed so the checklist can say "3 writes" beside an unticked one; only the ticked ones are summed and run.
    const preview = useQuery({
        ...trpc.push.preview.queryOptions({ environments: names, cells: request.cells }),
        enabled: names.length > 0,
        meta: { quiet: true },
    });
    const run = useMutation({
        ...trpc.push.run.mutationOptions(),
        meta: { quiet: true },
        onSuccess: (out) => setResult(out.environments),
    });

    const previews = new Map<string, PushPreview>((preview.data?.environments ?? []).map((p) => [p.name, p]));
    const refused = new Set((preview.data?.environments ?? []).filter((p) => p.refused !== null).map((p) => p.name));
    const effectiveTicked = new Set([...ticked].filter((name) => !refused.has(name)));

    const selected = names
        .filter((n) => effectiveTicked.has(n))
        .map((n) => previews.get(n))
        .filter((p): p is PushPreview => p !== undefined);
    // Environments with nothing to write are never sent to push.run, so a 0-write tick can't
    // come back "refused: nothing to do".
    const pushable = selected.filter((p) => writesOf(p) > 0);
    const live = canPush(selected) && !preview.isPending && !run.isPending && (writeAccess.me.data?.canApply ?? false);

    const toggle = (name: string) => setTicked((cur) => toggled(cur, name));

    return {
        names,
        previews,
        previewPending: preview.isPending,
        previewError: preview.error,
        ticked: effectiveTicked,
        onToggle: toggle,
        advisory: writeAccess.advisory,
        risky: riskyTicked(writeAccess.environments.data ?? [], effectiveTicked),
        note,
        onNote: setNote,
        live,
        canApply: writeAccess.me.data?.canApply ?? true,
        running: run.isPending,
        runError: run.error,
        confirmLabel: pushable.length === 0 ? 'Push' : confirmLabel(pushable),
        onConfirm: () =>
            run.mutate({ environments: pushable.map((p) => p.name), cells: request.cells, note: note || null }),
        result,
    };
};

export type PushWorkflow = ReturnType<typeof usePushWorkflow>;
